export const isProductRoute = (route = {}) => {
  const routeName = (route.name || "").toString();
  const routePath = `${route.fullPath || route.path || ""}`;

  return routeName.startsWith("product-") || routePath.includes("/product/");
};

const getPathWithoutHash = (path = "") => {
  return (path || "").toString().split("#")[0];
};

const getPathnameAndQuery = (path = "") => {
  const [pathname, queryString = ""] = getPathWithoutHash(path).split("?");
  return {
    pathname: pathname || "",
    query: new URLSearchParams(queryString),
  };
};

const joinPathSegments = (segments = []) => {
  return `/${segments.filter(Boolean).join("/")}`.replace(/\/+/g, "/");
};

const getNormalizedPathname = (path = "") => {
  return (path || "").replace(/\/+$/, "");
};

const normalizeLegacyPathToProductPath = (path = "") => {
  const { pathname, query } = getPathnameAndQuery(path);
  const segments = pathname.split("/").filter(Boolean);

  if (!pathname || pathname.includes("/product/")) {
    return getNormalizedPathname(pathname);
  }

  if (segments[0] === "live" && segments[1] === "page" && segments[2]) {
    return getNormalizedPathname(`/product/page/${segments[2]}`);
  }

  if (segments[0] !== "category" || !segments[1] || segments[2] !== "live") {
    return getNormalizedPathname(pathname);
  }

  const categoryId = segments[1];
  const liveSegments = segments.slice(3);
  const liveSection = liveSegments[0] || "";

  if (!liveSection) {
    return getNormalizedPathname(pathname);
  }

  if (liveSection === "display-homes") {
    const propertyId = liveSegments[2] === "property" ? liveSegments[3] : "";
    return buildProductDisplayHomesPath(categoryId, propertyId);
  }

  if (liveSection === "packages") {
    return buildProductPackagesPath(categoryId, liveSegments[1] || "");
  }

  if (liveSection === "dwellings") {
    const dwellingType = query.get("dwellingType") || "";
    return buildProductDwellingsPath(categoryId, dwellingType, liveSegments[1] || "");
  }

  if (liveSection === "turnkeys") {
    return buildProductTurnkeysPath(categoryId, "", liveSegments[1] || "");
  }

  if (liveSection === "carousel") {
    return buildProductCarouselPath(categoryId, liveSegments[1] || "", liveSegments[2] || "");
  }

  const mappedMasterplanBasePath = buildProductMasterplanPath(categoryId, liveSection);
  return getNormalizedPathname(joinPathSegments([mappedMasterplanBasePath, ...liveSegments.slice(1)]));
};

const getEquivalentPathVariants = (path = "") => {
  const { pathname } = getPathnameAndQuery(path);
  const normalizedPathname = getNormalizedPathname(pathname);
  const normalizedProductPath = normalizeLegacyPathToProductPath(path);

  return [...new Set([normalizedPathname, normalizedProductPath].filter(Boolean))];
};

const doesPathMatchTarget = (pathname = "", targetPath = "") => {
  if (!pathname || !targetPath) return false;
  if (pathname === targetPath) return true;

  return pathname.startsWith(`${targetPath}/`);
};

export const isRouteActiveForPath = (route = {}, targetPath = "") => {
  const routePath = `${route.fullPath || route.path || ""}`;
  const redirectedFromPath = `${route.redirectedFrom || ""}`;
  const routeVariants = [...new Set([
    ...getEquivalentPathVariants(routePath),
    ...getEquivalentPathVariants(redirectedFromPath),
  ])];
  const targetVariants = getEquivalentPathVariants(targetPath);

  return routeVariants.some(routeVariant => {
    return targetVariants.some(targetVariant => doesPathMatchTarget(routeVariant, targetVariant));
  });
};

export const isLivingEntryRoute = (routeName) => {
  return ["product-living-entry", "product-living-entry-type", "product-living-entry-turnkey-type"].includes(routeName);
};

export const isLivingOptionRoute = (routeName) => {
  return ["product-living-option", "product-living-option-type", "product-living-option-turnkey-type"].includes(routeName);
};

export const isSubsectionRoute = (routeName) => {
  return ["product-subsection", "product-subsection-type", "product-subsection-turnkey-type"].includes(routeName);
};

export const isPropertyRoute = (routeName) => {
  return ["product-property", "product-property-type", "product-property-turnkey-type"].includes(routeName);
};

export const isSubsectionOrPropertyRoute = (routeName) => {
  return isSubsectionRoute(routeName) || isPropertyRoute(routeName);
};

export const normalizeLivingPathway = (pathway = "masterplan") => {
  switch ((pathway || "").toString().toLowerCase()) {
    case "land":
      return "land";
    case "turnkey":
    case "turnkeys":
      return "turnkeys";
    case "house":
    case "houses":
      return "houses";
    case "townhome":
    case "townhomes":
    case "townhouse":
    case "townhouses":
      return "townhouses";
    case "terrace":
    case "terraces":
      return "terraces";
    case "villa":
    case "villas":
      return "villas";
    case "apartment":
    case "apartments":
      return "apartments";
    default:
      return "masterplan";
  }
};

export const getLivingPathway = (route = {}) => {
  if (route.params && route.params.pathway) return normalizeLivingPathway(route.params.pathway);
  if (route.params && route.params.turnkeyType) return "turnkeys";
  if (route.params && route.params.type) return normalizeLivingPathway(route.params.type);
  if (isProductRoute(route)) return "masterplan";

  return normalizeLivingPathway();
};

export const getProductTypeSegment = (pathway = "masterplan", allowCustom = false) => {
  if ((pathway || "").toString().toLowerCase() === "turnkeys") {
    return "turnkeys";
  }

  switch (normalizeLivingPathway(pathway)) {
    case "land":
      return "land";
    case "houses":
      return "house";
    case "townhouses":
      return "townhouse";
    case "terraces":
      return "terrace";
    case "villas":
      return "villa";
    case "apartments":
      return "apartment";
    default:
      return allowCustom ? encodeURIComponent((pathway || "").toString().toLowerCase()) : "";
  }
};

export const getCanonicalProductType = (type = "") => {
  switch ((type || "").toString().toLowerCase()) {
    case "land":
      return "land";
    case "house":
    case "houses":
      return "house";
    case "townhome":
    case "townhomes":
    case "townhouse":
    case "townhouses":
      return "townhouse";
    case "terrace":
    case "terraces":
      return "terrace";
    case "villa":
    case "villas":
      return "villa";
    case "apartment":
    case "apartments":
      return "apartments";
    case "turnkey":
    case "turnkeys":
      return "turnkeys";
    default:
      return (type || "").toString().toLowerCase();
  }
};

const getProductTypeVariants = (type = "") => {
  const canonicalType = getCanonicalProductType(type);
  if (!canonicalType) return [];

  const variants = new Set([canonicalType]);

  if (canonicalType.endsWith("ies") && canonicalType.length > 3) {
    variants.add(`${canonicalType.slice(0, -3)}y`);
  }

  if (canonicalType.endsWith("es") && canonicalType.length > 2) {
    variants.add(canonicalType.slice(0, -2));
  }

  if (canonicalType.endsWith("s") && canonicalType.length > 1) {
    variants.add(canonicalType.slice(0, -1));
  }

  if (!canonicalType.endsWith("s")) {
    if (canonicalType.endsWith("y") && canonicalType.length > 1) {
      variants.add(`${canonicalType.slice(0, -1)}ies`);
    }

    variants.add(`${canonicalType}s`);

    if (canonicalType.endsWith("ch") || canonicalType.endsWith("sh") || canonicalType.endsWith("x") || canonicalType.endsWith("z") || canonicalType.endsWith("s")) {
      variants.add(`${canonicalType}es`);
    }
  }

  return [...variants].filter(Boolean);
};

export const doProductTypesMatch = (leftType = "", rightType = "") => {
  const leftVariants = getProductTypeVariants(leftType);
  const rightVariants = getProductTypeVariants(rightType);

  if (!leftVariants.length || !rightVariants.length) return false;

  return leftVariants.some((variant) => rightVariants.includes(variant));
};

export const getPropertyTypeFromProperty = (property = {}) => {
  if (!property.dwelling || !property.dwelling.type) return "land";

  return getCanonicalProductType(property.dwelling.type);
};

export const getDwellingTypeFromDwelling = (dwelling = {}) => {
  return getCanonicalProductType(dwelling.type);
};

export const getLivingTurnkeyType = (route = {}) => {
  return getCanonicalProductType(route.params && route.params.turnkeyType);
};

export const isProductCarouselTurnkeysRoute = (route = {}) => {
  const routePath = `${route.fullPath || route.path || ""}`;
  return routePath.includes("/product/carousel/turnkeys");
};

export const isProductCarouselPackagesRoute = (route = {}) => {
  const routePath = `${route.fullPath || route.path || ""}`;
  return routePath.includes("/product/carousel/packages");
};

export const buildProductMasterplanPath = (categoryId, type = "", turnkeyType = "") => {
  const normalizedType = normalizeLivingPathway(type);
  const basePath = `/category/${categoryId}/product/masterplan`;

  if (normalizedType === "turnkeys") {
    const turnkeyTypeSegment = getProductTypeSegment(getCanonicalProductType(turnkeyType), true);
    return `${basePath}/turnkeys${turnkeyTypeSegment ? `/${turnkeyTypeSegment}` : ""}`;
  }

  const productTypeSegment = normalizedType === "masterplan" ? "" : getProductTypeSegment(normalizedType);
  return `${basePath}${productTypeSegment ? `/${productTypeSegment}` : ""}`;
};

export const buildProductPagePath = (pageId) => {
  return `/product/page/${pageId}`;
};

export const buildProductPackagesPath = (categoryId, packageId = "", packageType = "") => {
  const productTypeSegment = getProductTypeSegment(getCanonicalProductType(packageType), true);
  const basePath = `/category/${categoryId}/product/carousel/packages${productTypeSegment ? `/${productTypeSegment}` : ""}`;
  return packageId ? `${basePath}/${packageId}` : basePath;
};

export const buildProductDisplayHomesPath = (categoryId, propertyId = "") => {
  const basePath = `/category/${categoryId}/product/displayhomes`;
  return propertyId ? `${basePath}/property/${propertyId}` : basePath;
};

export const buildProductDwellingsPath = (categoryId, dwellingType = "", dwellingId = "") => {
  const productTypeSegment = getProductTypeSegment(getCanonicalProductType(dwellingType), true);
  const basePath = `/category/${categoryId}/product/carousel/dwellings${productTypeSegment ? `/${productTypeSegment}` : ""}`;
  return dwellingId ? `${basePath}/${dwellingId}` : basePath;
};

export const buildProductTurnkeysPath = (categoryId, productType = "", propertyId = "") => {
  const productTypeSegment = getProductTypeSegment(getCanonicalProductType(productType), true);
  const basePath = `/category/${categoryId}/product/carousel/turnkeys${productTypeSegment ? `/${productTypeSegment}` : ""}`;
  return propertyId ? `${basePath}/${propertyId}` : basePath;
};

export const buildProductCarouselPath = (categoryId, productType = "", propertyId = "") => {
  const canonicalProductType = getCanonicalProductType(productType);
  const isTurnkeysRoute = canonicalProductType === "turnkeys";
  const productTypeSegment = isTurnkeysRoute ? "" : getProductTypeSegment(canonicalProductType);
  const basePath = `/category/${categoryId}/product/carousel${isTurnkeysRoute ? "/turnkeys" : ""}${productTypeSegment ? `/${productTypeSegment}` : ""}`;
  return propertyId ? `${basePath}/${propertyId}` : basePath;
};

export const getLivingBasePath = (route, categoryId = route.params.categoryId) => {
  const pathway = getLivingPathway(route);

  if (isProductRoute(route)) {
    return buildProductMasterplanPath(categoryId, pathway, getLivingTurnkeyType(route));
  }

  return `/category/${categoryId}/live/${pathway}`;
};

export const buildLivingOptionPath = (route, livingOptionId, categoryId = route.params.categoryId) => {
  return `${getLivingBasePath(route, categoryId)}/${livingOptionId}`;
};

export const buildSubsectionPath = (route, livingOptionId, subsectionId, query = "", categoryId = route.params.categoryId) => {
  return `${buildLivingOptionPath(route, livingOptionId, categoryId)}/subsection/${subsectionId}${query}`;
};

export const buildPropertyPath = (route, livingOptionId, subsectionId, propertyId, query = "", categoryId = route.params.categoryId) => {
  return `${buildSubsectionPath(route, livingOptionId, subsectionId, "", categoryId)}/property/${propertyId}${query}`;
};

export const buildDwellingsPath = (route, categoryId = route.params.categoryId) => {
  if (isProductRoute(route)) {
    const dwellingType = getCanonicalProductType((route.params && route.params.dwellingType) || (route.params && route.params.productType) || (route.params && route.params.turnkeyType) || (route.params && route.params.type));
    return buildProductDwellingsPath(categoryId, dwellingType);
  }

  return `/category/${categoryId}/live/dwellings`;
};

export const buildDwellingsModalPath = (route, dwellingId, categoryId = route.params.categoryId) => {
  return `${buildDwellingsPath(route, categoryId)}/${dwellingId}`;
};

export const buildPackagesPath = (route, categoryId = route.params.categoryId) => {
  if (isProductRoute(route)) {
    const packageType = getCanonicalProductType(route.params && route.params.packageType);
    return buildProductPackagesPath(categoryId, "", packageType);
  }

  return `/category/${categoryId}/live/packages`;
};

export const buildPackagesModalPath = (route, packageId, categoryId = route.params.categoryId) => {
  return `${buildPackagesPath(route, categoryId)}/${packageId}`;
};

export const buildCarouselBasePath = (route, categoryId = route.params.categoryId) => {
  if (route.name === "dwellings" || route.name === "dwelling-modal") {
    return buildDwellingsPath(route, categoryId);
  }

  if (route.name === "packages" || route.name === "package-modal") {
    return buildPackagesPath(route, categoryId);
  }

  if (isProductRoute(route)) {
    if (isProductCarouselTurnkeysRoute(route)) {
      return buildProductTurnkeysPath(categoryId, route.params && route.params.productType);
    }

    return buildProductCarouselPath(categoryId, route.params && route.params.productType);
  }

  const productType = route.params && route.params.productType;
  return `/category/${categoryId}/live/carousel${productType ? `/${productType}` : ""}`;
};

export const buildCarouselModalPath = (route, id, categoryId = route.params.categoryId) => {
  return `${buildCarouselBasePath(route, categoryId)}/${id}`;
};
